import { sameTile } from "./direction";
import { GHOST_ORDER, Ghost, Rng, waveAt, type GhostSnapshot, type TargetInputs } from "./ghost";
import { Maze, key } from "./maze";
import { Player } from "./player";
import type { Direction, GameStatus, GhostMode, GhostName, Tile } from "./types";

/**
 * Tuning, in tiles per second and seconds.
 *
 * Slower than the arcade on purpose: the camera is isometric and the models are
 * physical, so arcade speed reads as a blur. The *ratios* between player and
 * ghost are kept, since that is what makes the chase feel right.
 */
export const TUNING = {
  playerSpeed: 5.0,
  ghostSpeed: 4.6,
  /** The arcade slows ghosts in the tunnel; that is what makes it an escape. */
  ghostTunnelSpeed: 2.3,
  frightenedSpeed: 3.0,
  /** Eyes returning to the den move fast, to get back into play. */
  eatenSpeed: 9.0,
  frightenedSeconds: 8,
  /** How long the flashing "about to un-frighten" warning lasts. */
  frightenedWarningSeconds: 2,
  storyPointValue: 10,
  quotaResetValue: 50,
  /** 200, 400, 800, 1600 for successive slop caught on one 5h Quota Reset. */
  slopValues: [200, 400, 800, 1600] as const,
  startingLives: 3,
  readySeconds: 2,
  dyingSeconds: 1.6,
  /** A ghost waiting in the den leaves anyway after this long. */
  denTimeoutSeconds: 4,
} as const;

export interface ScoreEvent {
  readonly kind: "storyPoint" | "quotaReset" | "slop" | "death" | "levelComplete";
  readonly points: number;
  readonly tile: Tile;
}

export interface GameSnapshot {
  readonly status: GameStatus;
  readonly score: number;
  readonly lives: number;
  readonly level: number;
  readonly mode: GhostMode;
  readonly storyPointsLeft: number;
  readonly storyPointsTotal: number;
  readonly frightenedRemaining: number;
  readonly player: {
    readonly x: number;
    readonly y: number;
    readonly dir: Direction;
    readonly tile: Tile;
    readonly blocked: boolean;
    /**
     * The turn asked for that the maze has not allowed yet.
     *
     * Exposed so a driver can tell "the input never arrived" apart from "the
     * input arrived and the astronaut is still in a corridor that cannot turn
     * that way". The touch controls are tested on exactly that distinction.
     */
    readonly queued: Direction | null;
  };
  readonly ghosts: readonly GhostSnapshot[];
  readonly elapsed: number;
}

export interface GameOptions {
  readonly maze?: Maze;
  readonly seed?: number;
  readonly lives?: number;
}

/**
 * The whole game, with no rendering and no I/O.
 *
 * Everything is driven by `update(dt)`, so a test can run a full level in a
 * millisecond and the renderer is just a reader of `snapshot()`.
 */
export class Game {
  readonly maze: Maze;
  readonly player: Player;
  readonly ghosts: readonly Ghost[];

  status: GameStatus = "ready";
  score = 0;
  lives: number;
  level = 1;
  elapsed = 0;

  /** Consumed by the renderer for sounds and pop-ups; cleared each update. */
  events: ScoreEvent[] = [];

  private readonly storyPoints = new Set<string>();
  private readonly quotaResets = new Set<string>();
  private readonly storyPointsTotal: number;
  private readonly rng: Rng;

  private modeElapsed = 0;
  private statusTimer: number = TUNING.readySeconds;
  private frightenedRemaining = 0;
  private slopEatenThisReset = 0;
  private dotsEaten = 0;
  private denTimer = 0;

  constructor(options: GameOptions = {}) {
    this.maze = options.maze ?? new Maze();
    this.rng = new Rng(options.seed ?? 0x5eed);
    this.lives = options.lives ?? TUNING.startingLives;
    this.player = new Player(this.maze, this.maze.spawns.player);
    this.ghosts = GHOST_ORDER.map(
      (cfg) => new Ghost(this.maze, cfg, this.maze.spawns[cfg.name], cfg.name === "blinky" ? "hunting" : "house"),
    );

    const { storyPoints, quotaResets } = this.maze.pelletTiles();
    for (const t of storyPoints) this.storyPoints.add(key(t));
    for (const t of quotaResets) this.quotaResets.add(key(t));
    this.storyPointsTotal = this.storyPoints.size + this.quotaResets.size;
  }

  get mode(): GhostMode {
    if (this.frightenedRemaining > 0) return "frightened";
    return waveAt(this.modeElapsed).mode;
  }

  /** Seconds left on the current 5h Quota Reset, or 0 when none is running. */
  get frightenedLeft(): number {
    return this.frightenedRemaining;
  }

  get storyPointsLeft(): number {
    return this.storyPoints.size + this.quotaResets.size;
  }

  hasStoryPoint(tile: Tile): boolean {
    return this.storyPoints.has(key(tile));
  }

  hasQuotaReset(tile: Tile): boolean {
    return this.quotaResets.has(key(tile));
  }

  /** Every pellet still on the deck, for the renderer to build its instances. */
  remainingPellets(): { storyPoints: Tile[]; quotaResets: Tile[] } {
    const parse = (k: string): Tile => {
      const [x, y] = k.split(",");
      return { x: Number(x), y: Number(y) };
    };
    return {
      storyPoints: [...this.storyPoints].map(parse),
      quotaResets: [...this.quotaResets].map(parse),
    };
  }

  steer(dir: Direction): void {
    if (this.status === "playing") this.player.steer(dir);
    // Nudging a direction during the countdown should carry into the round.
    else if (this.status === "ready") this.player.steer(dir);
  }

  /** Advance the simulation by `dt` seconds. */
  update(dt: number): void {
    this.events = [];
    if (dt <= 0) return;
    this.elapsed += dt;

    switch (this.status) {
      case "ready":
        this.statusTimer -= dt;
        if (this.statusTimer <= 0) this.status = "playing";
        return;
      case "dying":
        this.statusTimer -= dt;
        if (this.statusTimer <= 0) this.afterDeath();
        return;
      case "levelComplete":
      case "gameOver":
        return;
      case "playing":
        break;
    }

    this.stepPlaying(dt);
  }

  private stepPlaying(dt: number): void {
    if (this.frightenedRemaining > 0) {
      this.frightenedRemaining = Math.max(0, this.frightenedRemaining - dt);
      if (this.frightenedRemaining === 0) this.endFrightened();
    } else {
      const before = waveAt(this.modeElapsed).index;
      this.modeElapsed += dt;
      // A scatter/chase boundary forces every hunting ghost to turn around.
      if (waveAt(this.modeElapsed).index !== before) this.forceReverse();
    }

    this.player.advance(TUNING.playerSpeed * dt);
    this.consumePellet();
    // Collide before and after the ghosts move: two actors closing head-on can
    // otherwise swap tiles within one step and pass straight through.
    if (this.resolveCollisions()) return;
    this.moveGhosts(dt);
    this.releaseGhosts(dt);
    if (this.resolveCollisions()) return;

    if (this.storyPointsLeft === 0) {
      this.status = "levelComplete";
      this.events.push({ kind: "levelComplete", points: 0, tile: this.player.tile });
    }
  }

  private targetInputs(): TargetInputs {
    const blinky = this.ghosts.find((g) => g.name === "blinky")!;
    return { playerTile: this.player.tile, playerDir: this.player.dir, blinkyTile: blinky.tile };
  }

  private moveGhosts(dt: number): void {
    const mode = this.mode;
    const inputs = this.targetInputs();
    for (const ghost of this.ghosts) {
      ghost.advance(this.ghostSpeed(ghost) * dt, mode, inputs, this.rng);
    }
  }

  private ghostSpeed(ghost: Ghost): number {
    if (ghost.state === "eaten") return TUNING.eatenSpeed;
    if (ghost.state === "house" || ghost.state === "leaving") return TUNING.ghostSpeed * 0.5;
    if (ghost.frightened) return TUNING.frightenedSpeed;
    if (ghost.tile.y === this.maze.tunnelRow) return TUNING.ghostTunnelSpeed;
    return TUNING.ghostSpeed;
  }

  /**
   * Let the next waiting ghost out.
   *
   * The arcade gates this on a per-ghost dot counter, with a timer as the
   * backstop so a player who stops eating cannot stall the ghosts forever.
   */
  private releaseGhosts(dt: number): void {
    this.denTimer += dt;
    for (const ghost of this.ghosts) {
      if (ghost.state !== "house") continue;
      if (this.dotsEaten >= ghost.dotLimit || this.denTimer >= TUNING.denTimeoutSeconds) {
        ghost.release();
        this.denTimer = 0;
        return; // one at a time, in GHOST_ORDER
      }
    }
  }

  private consumePellet(): void {
    const tile = this.player.tile;
    const k = key(tile);
    if (this.storyPoints.delete(k)) {
      this.dotsEaten += 1;
      this.score += TUNING.storyPointValue;
      this.events.push({ kind: "storyPoint", points: TUNING.storyPointValue, tile });
      return;
    }
    if (this.quotaResets.delete(k)) {
      this.dotsEaten += 1;
      this.score += TUNING.quotaResetValue;
      this.events.push({ kind: "quotaReset", points: TUNING.quotaResetValue, tile });
      this.beginFrightened();
    }
  }

  private beginFrightened(): void {
    this.frightenedRemaining = TUNING.frightenedSeconds;
    this.slopEatenThisReset = 0;
    for (const ghost of this.ghosts) {
      if (ghost.state === "hunting") {
        ghost.frightened = true;
        ghost.pendingReverse = true;
      }
    }
  }

  private endFrightened(): void {
    for (const ghost of this.ghosts) ghost.frightened = false;
  }

  private forceReverse(): void {
    for (const ghost of this.ghosts) {
      if (ghost.state === "hunting") ghost.pendingReverse = true;
    }
  }

  /** Returns true when the round ended on this check. */
  private resolveCollisions(): boolean {
    const playerTile = this.player.tile;
    for (const ghost of this.ghosts) {
      if (ghost.state === "eaten" || ghost.state === "house" || ghost.state === "leaving") continue;
      // Tile equality alone lets a ghost brush past at speed; also accept a
      // near-miss in continuous space so contact is never silently skipped.
      const close = Math.abs(ghost.x - this.player.x) + Math.abs(ghost.y - this.player.y) < 0.75;
      if (!sameTile(ghost.tile, playerTile) && !close) continue;

      if (ghost.frightened) {
        const idx = Math.min(this.slopEatenThisReset, TUNING.slopValues.length - 1);
        const points = TUNING.slopValues[idx]!;
        this.slopEatenThisReset += 1;
        this.score += points;
        ghost.state = "eaten";
        ghost.frightened = false;
        this.events.push({ kind: "slop", points, tile: ghost.tile });
        continue;
      }
      this.die();
      return true;
    }
    return false;
  }

  private die(): void {
    this.lives -= 1;
    this.status = "dying";
    this.statusTimer = TUNING.dyingSeconds;
    // Clear the flag too, not just the clock: zeroing the timer alone leaves
    // every slop stuck looking (and scoring) as frightened through the death
    // animation, because nothing else ever clears it.
    this.frightenedRemaining = 0;
    this.endFrightened();
    this.events.push({ kind: "death", points: 0, tile: this.player.tile });
  }

  private afterDeath(): void {
    if (this.lives <= 0) {
      this.status = "gameOver";
      return;
    }
    this.player.reset(this.maze.spawns.player);
    for (const ghost of this.ghosts) {
      ghost.reset(ghost.name === "blinky" ? "hunting" : "house");
    }
    this.modeElapsed = 0;
    this.denTimer = 0;
    this.status = "ready";
    this.statusTimer = TUNING.readySeconds;
  }

  snapshot(): GameSnapshot {
    return {
      status: this.status,
      score: this.score,
      lives: this.lives,
      level: this.level,
      mode: this.mode,
      storyPointsLeft: this.storyPointsLeft,
      storyPointsTotal: this.storyPointsTotal,
      frightenedRemaining: this.frightenedRemaining,
      player: {
        x: this.player.x,
        y: this.player.y,
        dir: this.player.dir,
        tile: this.player.tile,
        blocked: this.player.blocked,
        queued: this.player.queued,
      },
      ghosts: this.ghosts.map((g) => g.snapshot()),
      elapsed: this.elapsed,
    };
  }

  /** The ghost by name, for tests and the renderer. */
  ghost(name: GhostName): Ghost {
    const found = this.ghosts.find((g) => g.name === name);
    if (found === undefined) throw new Error(`no ghost named ${name}`);
    return found;
  }
}
